import {
  Archive,
  ArrowCounterClockwise,
  Code,
  MagnifyingGlass,
  Pencil,
  Trash,
  X,
} from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { useCallback, useEffect, useState } from 'react';
import {
  AutoCompleteMultiSelectBox,
  Option as MultiSelectOption,
} from 'src/components/AutoCompleteMultiSelectBox';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Badge } from 'src/components/Badge';
import { CopyableField } from 'src/components/CopyableField';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { Option } from 'src/components/SelectBox';
import { SideDropActions } from 'src/components/SideDropActions';
import Table, { Row } from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import { Agent } from 'src/interfaces/agent.interface';
import {
  Number,
  NumberTags,
  NumberActiveState,
} from 'src/interfaces/number.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { getFirstAndLastFourUUID, processPhoneNumber } from 'src/utils/number';
import {
  buildTagOptions,
  buildTagOptionsWithQuery,
  mergeTagOptions,
} from 'src/components/tag/tag.helpers';
import { BuyNumber } from './BuyNumber';
import { CallPacingControls } from './CallPacingControls';
import { LinkNumber } from './LinkNumber';
import { AccountConnectionOption } from './numbersTypes';
import { providerOptions as providerOptionsStub } from 'src/stubs/numbers.stub';
import Checkbox from 'src/components/Checkbox';
import { BetaTag } from 'src/components/BetaTag';
import { SegmentedButtons } from 'src/components/SegmentedButtons';
import { WarningPanel } from 'src/components/WarningPanel';

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'number', label: 'Number', width: '20%', disableSorting: true },
  {
    key: 'inbound_agent',
    label: 'Agent',
    width: '12rem',
    disableSorting: true,
  },
  {
    key: 'telephony_provider',
    label: 'Telephony Provider',
    width: '10%',
    disableSorting: true,
  },
  {
    key: 'telephony_account_connection',
    label: 'Account Connection',
    width: '8rem',
    disableSorting: true,
  },
  {
    key: 'tags',
    label: 'Tags',
    width: '8rem',
    disableSorting: true,
  },
  { key: 'actions', label: '', width: '1rem', disableSorting: true },
];

const providerOptions: { [key: string]: string } = {
  account_connection_twilio: 'Twilio',
  account_connection_telnyx: 'Telnyx',
  account_connection_openai: 'OpenAI',
  account_connection_elevenlabs: 'ElevenLabs',
  account_connection_deepgram: 'Deepgram',
  account_connection_cartesia: 'Cartesia',
  account_connection_playht: 'PlayHT',
  account_connection_azure_speech: 'Azure Speech',
  account_connection_google: 'Google',
  account_connection_azure_llm: 'Azure LLM',
};

export const ListNumbers = ({
  selectedNumbers,
  setSelectedNumbers,
  readOnly = false,
}: {
  selectedNumbers?: Number[];
  setSelectedNumbers?: (numbers: Number[]) => void;
  readOnly?: boolean;
}) => {
  const { get, patch, remove, post } = useAuthedRequest();

  const notification = useNotification();

  const [rows, setRows] = useState<Number[]>([]);
  const [filter, setFilter] = useState('');
  const debouncedFilter = useDebounce(filter, { wait: 500 });
  const [selectedTagOptions, setSelectedTagOptions] = useState<
    MultiSelectOption[]
  >([]);
  const [filterTagOptions, setFilterTagOptions] = useState<MultiSelectOption[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const [modalBuyOpen, setModalBuyOpen] = useState(false);
  const [modalLinkOpen, setModalLinkOpen] = useState(false);
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [modalArchiveOpen, setModalArchiveOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const [agents, setAgents] = useState<Option[]>([]);
  const [accountConnections, setAccountConnections] = useState<
    AccountConnectionOption[]
  >([]);

  const [selectedNumber, setSelectedNumber] = useState<Number | null>(null);
  const [editTagOptions, setEditTagOptions] = useState<MultiSelectOption[]>([]);
  const [editSelectedTagOptions, setEditSelectedTagOptions] = useState<
    MultiSelectOption[]
  >([]);
  const [currentAgent, setCurrentAgent] = useState<Option | undefined>(
    undefined,
  );
  const [hasTelephonyConnection, setHasTelephonyConnection] = useState(false);
  const [activeState, setActiveState] = useState<NumberActiveState>(
    NumberActiveState.ACTIVE,
  );

  const { role } = useEnvironment();

  const getTagValues = (options: MultiSelectOption[]) =>
    options.map((option) => option.value);

  useEffect(() => {
    if (readOnly && activeState !== NumberActiveState.ACTIVE) {
      setActiveState(NumberActiveState.ACTIVE);
      setCurrentPage(1);
    }
  }, [activeState, readOnly]);

  const fetchNumberTagOptions = useCallback(
    async (query: string) => {
      const queryObject: QueryObject = {
        page: '1',
        size: '10',
      };
      const trimmedQuery = query.trim();
      if (trimmedQuery) {
        queryObject.filters = JSON.stringify({ startsWith: trimmedQuery });
      }
      const params = new URLSearchParams(queryObject as Record<string, string>);
      const data: NumberTags = await get(`/numbers/tags?${params.toString()}`);
      const tags = data.items || [];
      return buildTagOptionsWithQuery(tags, query);
    },
    [get],
  );

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
    };

    const activeStateFilter = readOnly ? NumberActiveState.ACTIVE : activeState;
    const tagsFilter = getTagValues(selectedTagOptions);
    queryObject.filters = JSON.stringify({
      number: filter ? filter : undefined,
      tags: tagsFilter.length ? tagsFilter : undefined,
      activeState: activeStateFilter,
    });

    if (sortConfig.key) {
      queryObject.sort_column = sortConfig.key;
      queryObject.sort_desc =
        sortConfig.direction === 'desc' ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/numbers?${query.toString()}`);

    const items = data.items.map((item: Number) => ({
      ...item,
      number: processPhoneNumber(item.number),
    }));

    setRows(items);

    setTotalItems(data.total);
    setLoading(false);
  };

  const handleFilterTagSearch = useCallback(
    async (query: string) => {
      const options = await fetchNumberTagOptions(query);
      const merged = mergeTagOptions(
        options,
        selectedTagOptions.map((option) => option.value),
      );
      setFilterTagOptions(merged);
    },
    [fetchNumberTagOptions, selectedTagOptions],
  );

  const handleEditTagSearch = useCallback(
    async (query: string) => {
      const options = await fetchNumberTagOptions(query);
      const merged = mergeTagOptions(
        options,
        editSelectedTagOptions.map((option) => option.value),
      );
      setEditTagOptions(merged);
    },
    [fetchNumberTagOptions, editSelectedTagOptions],
  );

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    sortConfig,
    debouncedFilter,
    size,
    selectedTagOptions,
    activeState,
  ]);

  useEffect(() => {
    handleFilterTagSearch('');
  }, [handleFilterTagSearch]);

  useEffect(() => {
    if (modalEditOpen) {
      handleEditTagSearch('');
    }
  }, [modalEditOpen, handleEditTagSearch]);

  useEffect(() => {
    if (selectedNumber) {
      const selectedTags = buildTagOptions(selectedNumber.tags || []);
      setEditSelectedTagOptions(selectedTags);
      setEditTagOptions((prev) =>
        mergeTagOptions(prev, selectedNumber.tags || []),
      );
    } else {
      setEditSelectedTagOptions([]);
    }
  }, [selectedNumber]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const submitFilter = () => {
    handlePageChange(1, size);
  };

  const handleTagBadgeClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    tag: string,
  ) => {
    event.stopPropagation();

    setSelectedTagOptions((prevSelected) => {
      const isAlreadySelected = prevSelected.some(
        (option) => option.value === tag,
      );
      if (isAlreadySelected) {
        // Remove the tag if it's already selected
        return prevSelected.filter((option) => option.value !== tag);
      }
      // Add the tag if it's not selected
      return [...prevSelected, { value: tag, label: tag }];
    });

    setFilterTagOptions((prevOptions) => mergeTagOptions(prevOptions, [tag]));
    setCurrentPage(1);
  };

  const renderTagBadges = (tags?: string[]) => {
    if (!tags || !tags.length) {
      return <span className="text-neutral-400">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <button
            key={tag}
            type="button"
            className="focus:outline-none"
            onClick={(event) => handleTagBadgeClick(event, tag)}
          >
            <Badge variant="primary" className="cursor-pointer">
              {tag}
            </Badge>
          </button>
        ))}
      </div>
    );
  };

  const openEditModal = (number: Number) => {
    setCurrentAgent(undefined);
    setSelectedNumber(number);
    setModalEditOpen(true);
  };

  const closeEditModal = () => {
    setSelectedNumber(null);
    setEquivalentCodeOpen(false);
    setModalEditOpen(false);
  };

  const cleanPhoneNumber = (phoneNumber: string) => {
    return `%2B${phoneNumber.replace(/[^0-9]/g, '')}`;
  };

  const confirmEditAction = async () => {
    if (selectedNumber) {
      setLoading(true);
      const tagsPayload = getTagValues(editSelectedTagOptions);

      try {
        await patch(
          `/numbers/${selectedNumber.id}?phone_number=${cleanPhoneNumber(selectedNumber.number)}`,
          {
            inbound_agent: selectedNumber.inbound_agent,
            sms_enabled: selectedNumber.sms_enabled,
            tags: tagsPayload,
          },
        );

        notification.success('Number updated successfully');
        setModalEditOpen(false);
        fetchData(currentPage, size);
        handleFilterTagSearch('');
        setSelectedNumber(null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        notification.error(
          `Failed to update number: ${error?.data?.detail || 'Unknown error'}`,
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleArchiveAction = async (number: Number) => {
    // If the number is associated with an agent, show the archive confirmation modal
    if (number.inbound_agent) {
      setSelectedNumber(number);
      setModalArchiveOpen(true);
      return;
    }

    // Otherwise, archive the number
    setLoading(true);

    const cleanedNumber = cleanPhoneNumber(number.number);

    await remove(`/numbers/detach/${cleanedNumber}`);

    setLoading(false);

    notification.success('Number successfully archived');
    fetchData(currentPage, size);
  };

  const handleArchiveConfirm = async () => {
    if (selectedNumber) {
      setLoading(true);

      const cleanedNumber = cleanPhoneNumber(selectedNumber.number);

      try {
        // Unassociate the number from any agent
        await patch(
          `/numbers/${selectedNumber.id}?phone_number=${cleanedNumber}`,
          {
            inbound_agent: null,
          },
        );

        // Archive the number
        await remove(`/numbers/detach/${cleanedNumber}`);

        notification.success('Number successfully archived');
        setModalArchiveOpen(false);
        fetchData(currentPage, size);
        setSelectedNumber(null);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        notification.error(
          `Failed to archive number: ${error?.data?.detail || 'Unknown error'}`,
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUnarchiveAction = async (number: Number) => {
    setLoading(true);

    const cleanedNumber = cleanPhoneNumber(number.number);

    await post(`/numbers/activate?phone_number=${cleanedNumber}`, {});

    setLoading(false);

    notification.success('Number unarchived successfully');
    fetchData(currentPage, size);
  };

  const openDeleteModal = (number: Number) => {
    setSelectedNumber(number);
    setModalDeleteOpen(true);
  };

  const confirmDeleteAction = async () => {
    if (selectedNumber) {
      setLoading(true);

      const cleanedNumber = cleanPhoneNumber(selectedNumber.number);

      await remove(`/numbers/cancel/${cleanedNumber}`);

      setLoading(false);

      notification.success('Number successfully deleted');
      setModalDeleteOpen(false);
      fetchData(currentPage, size);
      setSelectedNumber(null);
    }
  };

  const closeDeleteModal = () => {
    setModalDeleteOpen(false);
    setSelectedNumber(null);
  };

  const closeArchiveModal = () => {
    setModalArchiveOpen(false);
    setSelectedNumber(null);
  };

  const endpoint = selectedNumber
    ? `numbers/update?phone_number=${cleanPhoneNumber(selectedNumber.number)}`
    : ``;

  const fetchCurrentAgent = useCallback(async () => {
    if (selectedNumber?.inbound_agent) {
      const agent = await get(`/agents/${selectedNumber.inbound_agent}`);
      return agent;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNumber?.inbound_agent]);

  const searchAgents = useCallback(
    async (name: string) => {
      const queryObject: QueryObject = {
        ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/agents?${query.toString()}`);

      const agentOptions = data.items.map((agent: Agent) => ({
        value: agent.id,
        label: agent.name,
      }));

      setAgents(agentOptions);

      return agentOptions;
    },
    [get],
  );

  const fetchAccountConnections = async () => {
    const data = await get('/account-connections');

    const numberProviders = [
      'account_connection_twilio',
      'account_connection_telnyx',
    ];

    setAccountConnections(
      data.items.reduce(
        (
          acc: AccountConnectionOption[],
          accountConnection: AccountConnection,
        ) => {
          if (numberProviders.includes(accountConnection.type)) {
            acc.push({
              value: accountConnection.id || '',
              connectionType: accountConnection.type,
              label: `${providerOptions[accountConnection.type]} - ${getFirstAndLastFourUUID(accountConnection.id || '')}`,
            });
          }
          return acc;
        },
        [],
      ),
    );

    setHasTelephonyConnection(
      data.items.some(
        (accountConnection: AccountConnection) =>
          accountConnection.type === 'account_connection_twilio' ||
          accountConnection.type === 'account_connection_telnyx',
      ),
    );
  };

  const getProviderName = (provider: string) => {
    return (
      providerOptionsStub.find((item) => item.value === provider)?.label ||
      provider
    );
  };

  const fetchRelatedData = async () => {
    await Promise.all([fetchAccountConnections()]);
  };

  useEffect(() => {
    if (equivalentCodeOpen && !loading) {
      setEquivalentCodeOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNumber, equivalentCodeOpen]);

  useEffect(() => {
    fetchRelatedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function populateCurrentAgent() {
      if (selectedNumber?.inbound_agent) {
        const fetchedAgent = await fetchCurrentAgent();

        setCurrentAgent({
          value: fetchedAgent.id,
          label: fetchedAgent.name,
        });
      }
    }

    populateCurrentAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNumber?.inbound_agent]);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const onRowSelectionChange = (newSelection: number[]) => {
    const newSelectedNumbers = rows.filter((_, index) =>
      newSelection.includes(index),
    );
    setSelectedRows(newSelection);
    setSelectedNumbers?.(newSelectedNumbers);
  };

  useEffect(() => {
    if (selectedNumbers) {
      const rowsToSelect = rows.filter((row) =>
        selectedNumbers.some((number) => number.id === row.id),
      );

      setSelectedRows(rowsToSelect.map((row) => rows.indexOf(row)));
    }
  }, [selectedNumbers, rows]);

  return (
    <>
      <div>
        {!readOnly && (
          <div className="mb-6">
            <CallPacingControls />
          </div>
        )}
        <div className="flex flex-col gap-4 2xl:flex-row 2xl:justify-between mb-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end w-full">
            <div className="flex w-full md:w-96 self-end">
              <Can operation="read" subject={Subject.NUMBERS}>
                <Input
                  type="text"
                  required
                  placeholder="Search numbers"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      submitFilter();
                    }
                  }}
                  icon={<MagnifyingGlass size={20} />}
                  iconPosition="left"
                  inputAction={() => fetchData(currentPage, size)}
                />
              </Can>
            </div>

            <div className="w-full md:w-96">
              <AutoCompleteMultiSelectBox
                selectedOptions={selectedTagOptions}
                fetchOptions={handleFilterTagSearch}
                onSelectionChange={(options) => {
                  setSelectedTagOptions(options);
                  setCurrentPage(1);
                }}
                options={filterTagOptions}
                placeholder="Filter by tags"
                noOptionsText="No matching tags"
              />
            </div>
            {!readOnly && (
              <div>
                <SegmentedButtons
                  value={activeState}
                  onValueChange={(value) => {
                    setActiveState(value as NumberActiveState);
                    setCurrentPage(1);
                  }}
                  buttons={[
                    { value: NumberActiveState.ACTIVE, label: 'In Use' },
                    { value: NumberActiveState.INACTIVE, label: 'Archived' },
                  ]}
                />
              </div>
            )}
          </div>

          <div className="flex items-start gap-2 2xl:items-center 2xl:mt-0">
            <Can operation="create" subject={Subject.NUMBERS}>
              <Button
                className="whitespace-nowrap"
                onClick={() => setModalBuyOpen(true)}
              >
                New
              </Button>
            </Can>

            <Can operation="create" subject={Subject.NUMBERS}>
              <Tooltip
                content={
                  hasTelephonyConnection
                    ? ''
                    : 'Telephony connection is required to import numbers'
                }
                position="left"
              >
                <Button
                  disabled={!hasTelephonyConnection}
                  onClick={() => setModalLinkOpen(true)}
                  color={hasTelephonyConnection ? 'primary' : 'default'}
                  className="whitespace-nowrap"
                >
                  Link Existing
                </Button>
              </Tooltip>
            </Can>
          </div>
        </div>

        <Can operation="read" subject={Subject.NUMBERS}>
          <Table
            headers={headers}
            enableRowSelection={!!setSelectedNumbers}
            selectedRows={selectedRows}
            onRowSelectionChange={onRowSelectionChange}
            onSelectAllRows={(selectAll) => {
              const newSelection = selectAll ? rows.map((_, i) => i) : [];
              onRowSelectionChange(newSelection);
            }}
            rows={rows.map(
              (row) =>
                ({
                  id: (
                    <CopyableField value={row.id} notification={notification} />
                  ),
                  number: (
                    <CopyableField
                      value={row.number}
                      notification={notification}
                      stripWhitespaces
                    />
                  ),
                  inbound_agent: row.inbound_agent ? (
                    <div className="flex flex-col">
                      <span>{row.inbound_agent_name || 'Unknown'}</span>
                      <CopyableField
                        value={row.inbound_agent}
                        notification={notification}
                        className="mt-1 text-neutral-400"
                      />
                    </div>
                  ) : (
                    <span className="text-neutral-400">-</span>
                  ),
                  telephony_provider: getProviderName(row.telephony_provider),
                  telephony_account_connection:
                    row.telephony_account_connection ? (
                      <CopyableField
                        value={row.telephony_account_connection}
                        notification={notification}
                      />
                    ) : (
                      '-'
                    ),
                  tags: renderTagBadges(row.tags),
                  actions: (
                    <div
                      className="flex justify-center items-center"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <SideDropActions
                        zIndex={60}
                        options={
                          !readOnly
                            ? [
                                {
                                  label: 'Edit',
                                  Icon: Pencil,
                                  show:
                                    activeState === NumberActiveState.ACTIVE &&
                                    can(role, 'update', Subject.NUMBERS),
                                  onClick: () => openEditModal(row),
                                },
                                {
                                  label: 'Archive',
                                  Icon: Archive,
                                  show:
                                    activeState === NumberActiveState.ACTIVE &&
                                    can(role, 'update', Subject.NUMBERS),
                                  onClick: () => handleArchiveAction(row),
                                },
                                {
                                  label: 'Unarchive',
                                  Icon: ArrowCounterClockwise,
                                  show:
                                    activeState ===
                                      NumberActiveState.INACTIVE &&
                                    can(role, 'update', Subject.NUMBERS),
                                  onClick: () => handleUnarchiveAction(row),
                                },
                                {
                                  label: 'Delete',
                                  Icon: Trash,
                                  color: 'error',
                                  show: can(role, 'delete', Subject.NUMBERS),
                                  onClick: () => openDeleteModal(row),
                                },
                              ]
                            : []
                        }
                      />
                    </div>
                  ),
                }) as Row,
            )}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
            onRowClick={
              readOnly
                ? (index: number) => {
                    if (!setSelectedNumbers) return;
                    const row = rows[index];
                    if (!row) return;

                    if (selectedNumbers?.includes(row)) {
                      setSelectedNumbers(
                        selectedNumbers.filter(
                          (number) => number.id !== row.id,
                        ),
                      );
                      return;
                    }

                    setSelectedNumbers([...(selectedNumbers || []), row]);
                  }
                : activeState === NumberActiveState.ACTIVE
                  ? (index: number) => {
                      const number = rows[index];
                      if (number && can(role, 'update', Subject.NUMBERS)) {
                        openEditModal(number);
                      }
                    }
                  : undefined
            }
          />
        </Can>
      </div>

      <BuyNumber
        onClose={() => setModalBuyOpen(false)}
        refreshData={() => {
          fetchData(currentPage, size);
          handleFilterTagSearch('');
        }}
        modalOpen={modalBuyOpen}
        setModalOpen={setModalBuyOpen}
        agents={agents}
        accountConnections={accountConnections}
      />

      <LinkNumber
        onClose={() => setModalLinkOpen(false)}
        refreshData={() => {
          fetchData(currentPage, size);
          handleFilterTagSearch('');
        }}
        modalOpen={modalLinkOpen}
        setModalOpen={setModalLinkOpen}
        agents={agents}
        accountConnections={accountConnections}
      />

      {/* Edit Number Modal */}
      <Modal
        title="Edit Number"
        isOpen={modalEditOpen}
        onClose={() => closeEditModal()}
        actionButton={
          <Button onClick={() => confirmEditAction()} disabled={loading}>
            Save Number
          </Button>
        }
      >
        {selectedNumber && (
          <>
            <div className="flex justify-end mb-1">
              <Button
                onClick={() =>
                  equivalentCodeOpen
                    ? setEquivalentCodeOpen(false)
                    : setEquivalentCodeOpen(true)
                }
                className="flex items-center justify-center"
              >
                <Code className="mr-2" size={20} />
                Show equivalent code
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div>
                <Label>Current Number</Label>
                <Input type="text" value={selectedNumber.number} disabled />
              </div>

              <div>
                <Label>Select Agent</Label>
                <AutoCompleteSelectBox
                  key={currentAgent?.value}
                  defaultValue={currentAgent}
                  fetchOptions={searchAgents}
                  onChange={(value) =>
                    setSelectedNumber({
                      ...selectedNumber,
                      inbound_agent: value,
                    })
                  }
                  variant="outlined"
                  color="primary"
                  size="medium"
                  disabled={loading}
                />
              </div>
              <div>
                <Label>Tags</Label>
                <AutoCompleteMultiSelectBox
                  selectedOptions={editSelectedTagOptions}
                  fetchOptions={handleEditTagSearch}
                  onSelectionChange={(options) =>
                    setEditSelectedTagOptions(options)
                  }
                  options={editTagOptions}
                  placeholder="Add or search tags"
                  noOptionsText="No tags yet"
                />
              </div>
              {selectedNumber.telephony_provider === 'twilio' && (
                <div className="flex gap-2">
                  <Label>Enable SMS</Label>
                  <BetaTag size="small" />
                  <Checkbox
                    checked={selectedNumber.sms_enabled || false}
                    onChange={(newChecked) =>
                      setSelectedNumber({
                        ...selectedNumber,
                        sms_enabled: newChecked,
                      })
                    }
                    ariaLabel="Enable SMS messaging"
                  />
                </div>
              )}
            </div>

            {equivalentCodeOpen && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3>Equivalent Code</h3>

                  <X
                    className="cursor-pointer inline-block w-5 h-5"
                    onClick={() => setEquivalentCodeOpen(false)}
                  />
                </div>

                <EquivalentCode
                  payload={
                    selectedNumber
                      ? {
                          inbound_agent: selectedNumber.inbound_agent,
                          tags: getTagValues(editSelectedTagOptions),
                        }
                      : {}
                  }
                  endpoint={endpoint}
                  method="POST"
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Delete Number Modal */}
      <Modal
        title="Delete Number"
        isOpen={modalDeleteOpen}
        onClose={closeDeleteModal}
        actionButton={
          <Button
            color="error"
            onClick={() => confirmDeleteAction()}
            disabled={loading}
          >
            Delete Number
          </Button>
        }
      >
        <div className="space-y-4">
          {selectedNumber?.inbound_agent && (
            <WarningPanel
              show
              title="Deleting this number will unassign this number from the current agent"
            >
              <p>
                After deleting, calls to this number won't go to the current
                agent anymore.
              </p>
            </WarningPanel>
          )}
          <p className="text-sm text-neutral-600">
            Are you sure you want to delete this number? (
            {selectedNumber?.number})
          </p>
        </div>
      </Modal>

      {/* Archive Number Modal */}
      <Modal
        title="Archive Number"
        isOpen={modalArchiveOpen}
        onClose={closeArchiveModal}
        actionButton={
          <Button
            color="error"
            onClick={() => handleArchiveConfirm()}
            disabled={loading}
          >
            Archive
          </Button>
        }
      >
        <div className="space-y-4">
          <WarningPanel
            show
            title="Archiving will unassign this number from the current agent"
          >
            <p>
              After archiving, calls to this number won't go to the current
              agent anymore.
            </p>
          </WarningPanel>
          <p className="text-sm text-neutral-600">
            Are you sure you want to archive {selectedNumber?.number}?
          </p>
        </div>
      </Modal>
    </>
  );
};
